"use client"

// src/components/Analytics/LinkAnalytics.js
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { saveAs } from "file-saver"
import Papa from "papaparse"
import jsPDF from "jspdf"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts"
import api from "../../services/api"
// import { AuthContext } from "../../context/AuthContext"
// import MarketingBanner from "../Dashboard/MarketingBanner"
import CountryMap from "./CountryMap"
import MiniFooter from "../Layout/miniFooter"

const DEFAULT_RANGE = "30d"
const TOP_N = 5
const COLORS = ["#0d6efd", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6"]
const COLORS1 = ["#8b5cf6", "#ef4444", "#f59e0b", "#22c55e", "#0d6efd"]

const styles = {
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    paddingTop: "2rem",
    paddingBottom: "3rem",
  },
  card: {
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "1.25rem 1.5rem",
    borderBottom: "1px solid #f1f5f9",
    background: "#ffffff",
  },
  cardTitle: {
    margin: 0,
    fontSize: "0.9375rem",
    fontWeight: 600,
    color: "#1e293b",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  cardBody: {
    padding: "1.5rem",
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.025em",
    marginBottom: "0.5rem",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    padding: "0.25rem 0.625rem",
    borderRadius: "9999px",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem 1rem",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#475569",
  },
  buttonPrimary: {
    background: "#0d6efd",
    color: "#ffffff",
    border: "none",
  },
  rangeButton: {
    padding: "0.375rem 0.875rem",
    borderRadius: "6px",
    fontSize: "0.8125rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    border: "none",
  },
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        padding: "0.75rem 1rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <p style={{ margin: 0, fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>{label}</p>
      <p style={{ margin: 0, fontSize: "0.9375rem", fontWeight: 600, color: "#0f172a" }}>
        {payload[0].value.toLocaleString()} clicks
      </p>
    </div>
  )
}

export default function LinkAnalytics() {
  const { code } = useParams()
  // const { user: contextUser, setUser: setContextUser } = useContext(AuthContext)
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [userPlan, setUserPlan] = useState("free")
  const [access, setAccess] = useState(null)
  const [dateRange, setDateRange] = useState(DEFAULT_RANGE)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)

  // --- Normalization Helpers ---
  const normalizeObject = (obj) => {
    if (!obj) return []
    if (Array.isArray(obj)) {
      return obj.map((item) => ({
        name: String(item.name ?? item.key ?? item.source ?? "Unknown"),
        clicks: Number(item.clicks ?? item.value ?? item.count ?? 0),
      }))
    }
    if (typeof obj === "object") {
      return Object.entries(obj).map(([key, value]) => ({
        name: String(key),
        clicks: Number(value?.$numberInt ?? value ?? 0),
      }))
    }
    return [{ name: String(obj), clicks: Number(obj) || 0 }]
  }


  const normalizeGeoCountries = (geo = []) => {
  if (!Array.isArray(geo)) return []

  return geo.map((c) => ({
    code: c.code || c.name || "Unknown",
    name: c.name || c.name || "Unknown",
    clicks: Number(c.clicks ?? c.total ?? 0),
    regions: Array.isArray(c.regions)
      ? c.regions.map((r) => ({
          name: r.name || r.region || "Unknown",
          clicks: Number(r.clicks ?? r.total ?? 0),
          cities: Array.isArray(r.cities)
            ? r.cities.map((ct) => ({
                name: ct.name || ct.city || "Unknown",
                clicks: Number(ct.clicks ?? ct.total ?? 0),
              }))
            : [],
        }))
      : [],
  }))
}

const normalizeReferrers = (referrers) => {
  if (!referrers) return []

  // ✅ New schema: object map
  if (!Array.isArray(referrers)) {
    return Object.entries(referrers).map(([source, clicks]) => ({
      name: String(source || "Unknown"),
      clicks: Number(clicks || 0),
    }))
  }

  // ⚠️ Legacy schema: array
  return referrers.map((item) => ({
    name: String(item.source ?? "Unknown"),
    clicks: Number(item.clicks ?? item.count ?? 0),
  }))
}


  // const normalizeReferrers = (arr) => {
  //   if (!arr || !arr.length) return []
  //   return arr.map((item) => ({
  //     name: String(item.source ?? "Unknown"),
  //     clicks: Number(item.clicks ?? item.count ?? 0),
  //   }))
  // }

  const normalizeDaily = (arr) => {
    if (!arr || !arr.length) return []
    return arr.map((i) => ({
      name: i.date?.$date ? i.date.$date.slice(0, 10) : String(i.date ?? ""),
      clicks: Number(i.clicks ?? 0),
    }))
  }

  const safe = (arr) =>
    arr && arr.length
      ? arr.map((i) => ({
          name: String(i.name ?? "Unknown"),
          clicks: Number(i.clicks ?? 0),
        }))
      : [{ name: "No Data", clicks: 0 }]

  const calculateTrend = (arr) => {
    if (!arr || arr.length < 2) return { value: 0, trend: "neutral" }
    const last = arr[arr.length - 1].clicks
    const prev = arr[arr.length - 2].clicks
    const diff = last - prev
    const percent = prev ? Math.round((diff / prev) * 100) : 100
    return { value: percent, trend: diff > 0 ? "up" : diff < 0 ? "down" : "neutral" }
  }

  // --- Fetch Analytics ---
  useEffect(() => {
    if (!code) {
      setError("No link code provided.")
      setLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        const userRes = await api.get("/user/me")
        const currentUser = userRes.data?.data
        if (currentUser) {
          setUserPlan(currentUser.plan || "free")
          // setContextUser?.(currentUser)
        }

        const res = await api.get(`/links/analytics/${code}?range=${dateRange}`)
        if (!res.data.success) {
          setError(res.data.message || "Failed to load analytics.")
          return
        }
        setAccess(res.data.access || {})
        const d = res.data.data || {}
        
        setData({
          linkInfo: {
              originalUrl: d.linkInfo.originalUrl ?? "",
              shortCode: d.linkInfo.shortCode ?? "",
              shortUrl: d.linkInfo.shortUrl ?? "",
              isActive: d.linkInfo.isActive ?? true,
              createdAt: d.linkInfo.createdAt ?? "",
            },
          totalClicks: Number(d.totalClicks ?? 0),
          totalQrScans: Number(d.totalQrScans ?? 0),
          dailyClicks: normalizeDaily(d.daily),
          byCountry: normalizeGeoCountries(d.geo ?? d.byCountry),
          byDevice: normalizeObject(d.device ?? d.byDevice),
          os: normalizeObject(d.os ?? d.os),
          byBrowser: normalizeObject(d.browser ?? d.byBrowser),
          referrers: normalizeReferrers(d.referrers),
        })
        setLastUpdated(new Date())
      } catch (err) {
        console.error(err)
        setError("Failed to fetch analytics.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [code, dateRange])


  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(() => {
      setLoading(true)
      window.location.reload()
    }, 60000)
    return () => clearInterval(interval)
  }, [autoRefresh])

  // --- Downloads ---
  const downloadCSV = () => {
    if (!data) return
    const csv = Papa.unparse(data.dailyClicks)
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    saveAs(blob, `link-analytics-${code}.csv`)
  }

  const downloadPDF = () => {
    if (!data) return
    const doc = new jsPDF()
    doc.setFontSize(14)
    doc.text(`Link Analytics - ${data.linkInfo.originalUrl}`, 10, 10)
    data.dailyClicks.forEach((d, i) => {
      doc.text(`${d.name}: ${d.clicks} clicks`, 10, 20 + i * 10)
    })
    doc.save(`link-analytics-${code}.pdf`)
  }

  const StatCard = ({ icon, label, value, trend, color = "#6366f1" }) => (
    <div style={styles.card}>
      <div style={{ padding: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <p style={styles.statLabel}>{label}</p>
            <p style={styles.statValue}>{typeof value === "number" ? value.toLocaleString() : value}</p>
            {trend && (
              <span
                style={{
                  ...styles.badge,
                  background: trend.trend === "up" ? "#dcfce7" : trend.trend === "down" ? "#fee2e2" : "#f1f5f9",
                  color: trend.trend === "up" ? "#16a34a" : trend.trend === "down" ? "#dc2626" : "#64748b",
                  marginTop: "0.5rem",
                }}
              >
                {trend.trend === "up" ? "↑" : trend.trend === "down" ? "↓" : "−"} {Math.abs(trend.value)}% vs yesterday
              </span>
            )}
          </div>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: `${color}10`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.25rem",
            }}
          >
            {icon}
          </div>
        </div>
      </div>
    </div>
  )

  const BreakdownList = ({ data, title, icon, color = "#6366f1" }) => {
    const sorted = safe(data)
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, TOP_N)
    const maxClicks = Math.max(...sorted.map((d) => d.clicks), 1)

    return (
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h5 style={styles.cardTitle}>
            <span>{icon}</span> {title}
          </h5>
        </div>
        <div style={{ padding: "1rem 1.5rem" }}>
          {sorted.map((item, idx) => (
            <div key={idx} style={{ marginBottom: idx < sorted.length - 1 ? "1rem" : 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.375rem" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#334155" }}>{item.name}</span>
                <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#0f172a" }}>
                  {item.clicks.toLocaleString()}
                </span>
              </div>
              <div style={{ height: "6px", background: "#f1f5f9", borderRadius: "3px", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${(item.clicks / maxClicks) * 100}%`,
                    background: color,
                    borderRadius: "3px",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const UpgradeOverlay = ({ title, description, onUpgrade }) => (
  <div
    style={{
      position: "relative",
      height: "100%",
      minHeight: "260px",
      background: "#f8fafc",
      borderRadius: "16px",
      border: "1px dashed #c7d2fe",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "2rem",
    }}
  >
    <div>
      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🔒</div>
      <h4 style={{ margin: "0 0 0.25rem", color: "#0f172a" }}>{title}</h4>
      <p style={{ margin: "0 0 1rem", color: "#64748b", fontSize: "0.875rem" }}>
        {description}
      </p>
      <button
        onClick={onUpgrade}
        style={{ ...styles.button, ...styles.buttonPrimary }}
      >
        Upgrade Plan
      </button>
    </div>
  </div>
)


  const { linkInfo } = data || {}

  if (loading) {
    return (
      <div style={styles.page}>
        <div className="container">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "3px solid #e2e8f0",
                borderTopColor: "#0d6efd",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ marginTop: "1rem", color: "#64748b", fontSize: "0.9375rem" }}>Loading analytics...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div className="container">
          <div
            style={{
              ...styles.card,
              maxWidth: "480px",
              margin: "4rem auto",
              textAlign: "center",
              padding: "2.5rem",
            }}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: "#fef2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem",
                fontSize: "1.5rem",
              }}
            >
              ⚠️
            </div>
            <h3 style={{ margin: "0 0 0.5rem", color: "#0f172a", fontSize: "1.125rem", fontWeight: 600 }}>
              Something went wrong
            </h3>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.9375rem" }}>{error}</p>
            <button
              onClick={() => navigate(-1)}
              style={{ ...styles.button, ...styles.buttonPrimary, marginTop: "1.5rem" }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!data) return null

  const { totalClicks, totalQrScans, dailyClicks, byCountry, byDevice, os, byBrowser, referrers } = data

  const clicksTrend = calculateTrend(dailyClicks)
  const avgClicksPerDay = dailyClicks.length
    ? Math.round(dailyClicks.reduce((a, b) => a + b.clicks, 0) / dailyClicks.length)
    : 0;

  return (
    <>
    <div style={styles.page}>
      <div className="container">
        {/* Header */}
        <div style={{ marginBottom: "2rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            <div>
              <h1 style={{ margin: "0 0 0.375rem", fontSize: "1.5rem", fontWeight: 700, color: "#0f172a" }}>
                Link Analytics
              </h1>
              <p
              style={{
                  margin: 0,
                  fontSize: "0.975rem",
                  color: "#64748b",
                  maxWidth: "400px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
               >You're viewing analytics for - <b><small> <i style={{color: "#0d6efd"}}>{linkInfo?.shortUrl}</i> </small> </b></p>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.975rem",
                  color: "#64748b",
                  maxWidth: "400px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
              <small>{linkInfo?.originalUrl}</small>
              </p>
              <small>View <a style={{textDecoration: "none"}} href={`/link/p/${code}/stats`} target="_blank" rel="noopener noreferrer"><i>Public Stats</i></a> </small>
            </div>
            {lastUpdated && (
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <span style={{ fontSize: "0.8125rem", color: "#94a3b8" }}>
                  Updated {lastUpdated.toLocaleTimeString()}
                </span>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    style={{ accentColor: "#6366f1" }}
                  />
                  <span style={{ fontSize: "0.8125rem", color: "#64748b" }}>Auto-refresh</span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* {userPlan === "free" && (
          <MarketingBanner
            user={contextUser}
            onButtonClick={() => navigate("/account/upgrade")}
          />
        )} */}

        {userPlan !== "" && (
          <>
            {/* Controls Bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "1rem",
                marginBottom: "1.5rem",
                padding: "1rem",
                background: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button onClick={downloadCSV} style={styles.button}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  Export CSV
                </button>
                <button onClick={downloadPDF} style={styles.button}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                  </svg>
                  Export PDF
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  padding: "0.25rem",
                }}
              >
                {["7d", "30d", "90d"].map((r) => (
                  <button
                    key={r}
                    onClick={() => setDateRange(r)}
                    style={{
                      ...styles.rangeButton,
                      background: dateRange === r ? "#0d6efd" : "transparent",
                      color: dateRange === r ? "#ffffff" : "#64748b",
                    }}
                  >
                    {r === "7d" ? "7 days" : r === "30d" ? "30 days" : "90 days"}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="row g-4 mb-4">
              <div className="col-md-4">
                <StatCard icon="📊" label="Total Clicks" value={totalClicks} trend={clicksTrend} color="#6366f1" />
              </div>
              <div className="col-md-4">
                <StatCard icon="📱" label="QR Scans" value={totalQrScans} color="#22c55e" />
              </div>
              <div className="col-md-4">
                <StatCard icon="📈" label="Daily Average" value={avgClicksPerDay} color="#f59e0b" />
              </div>
            </div>

            {/* Main Chart */}
            <div style={{ ...styles.card, marginBottom: "1.5rem" }}>
              {!access?.timelineChart ? (
                <UpgradeOverlay
                  title="Timeline Analytics Locked"
                  description="Upgrade your plan to view click trends over time."
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <>
              <div style={styles.cardHeader}>
                <h5 style={styles.cardTitle}>
                  <span>📈</span> Clicks Over Time
                </h5>
              </div>
              <div style={{ ...styles.cardBody, height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={safe(dailyClicks)}>
                    <defs>
                      <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      fontSize={12}
                      tickLine={false}
                      axisLine={{ stroke: "#e2e8f0" }}
                    />
                    <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="clicks" stroke="#6366f1" strokeWidth={2} fill="url(#colorClicks)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              </>
               )}
            </div>

            {/* Charts Grid */}
            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div style={{ ...styles.card, height: "100%" }}>
                      {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Traffic by Country Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <>
                  <div style={styles.cardHeader}>
                    <h5 style={styles.cardTitle}>
                      <span>🌍</span> Traffic by Country
                    </h5>
                  </div>
                  <div style={{ ...styles.cardBody, height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={safe(byCountry).slice(0, 5)}
                          dataKey="clicks"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={4}
                        >
                          {safe(byCountry)
                            .slice(0, 5)
                            .map((entry, index) => (
                              <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value) => (
                            <span style={{ color: "#64748b", fontSize: "0.8125rem" }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    
                  </div>
                  </>
                  )}
                </div>
              </div>

                            <div className="col-lg-6">
                <div style={{ ...styles.card, height: "100%" }}>
                                {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Browser Usage Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <>
                  <div style={styles.cardHeader}>
                    <h5 style={styles.cardTitle}>
                      <span>🌐</span> Browser Usage
                    </h5>
                  </div>
                  <div style={{ ...styles.cardBody, height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={safe(byBrowser)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={true} vertical={false} />
                        <XAxis type="number" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          stroke="#94a3b8"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          width={80}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="clicks" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={24} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  </>
                  )}
                </div>
              </div>

              <div className="col-lg-6">
                <div style={{ ...styles.card, height: "100%" }}>
                                {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Device Breakdown Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
                ) : (
                <>
                  <div style={styles.cardHeader}>
                    <h5 style={styles.cardTitle}>
                      <span>💻</span> Device Breakdown
                    </h5>
                  </div>
                  <div style={{ ...styles.cardBody, height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={safe(byDevice)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={true} vertical={false} />
                        <XAxis type="number" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          stroke="#94a3b8"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          width={80}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="clicks" fill="#22c55e" radius={[0, 4, 4, 0]} barSize={24} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  </>
                  )}
                </div>
              </div>

              <div className="col-lg-6">
                <div style={{ ...styles.card, height: "100%" }}>
                                {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Device Breakdown Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
                ) : (
                <>
                  <div style={styles.cardHeader}>
                    <h5 style={styles.cardTitle}>
                      <span>🖥️</span> Platform Breakdown
                    </h5>
                  </div>
                  <div style={{ ...styles.cardBody, height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={safe(os).slice(0, 5)}
                          dataKey="clicks"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={4}
                        >
                          {safe(os)
                            .slice(0, 5)
                            .map((entry, index) => (
                              <Cell key={index} fill={COLORS1[index % COLORS1.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value) => (
                            <span style={{ color: "#64748b", fontSize: "0.8125rem" }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  </>
                  )}
                </div>
              </div>

              <div className="col-lg-6">
                              {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Top Referrers Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <BreakdownList data={referrers} title="Top Referrers" icon="🔗" color="#0d6efd" />
              )}
              </div>
            </div>

            {/* Country Map */}
            <div className="col-lg-12 mb-4">
              {access?.geo ? (
                <CountryMap data={byCountry} />
                  ) : (
                  <UpgradeOverlay
                    title="Map View Locked"
                    description="Upgrade to see traffic distribution by country."
                    onUpgrade={() => navigate("/account/upgrade")}
                  />
                )}

            </div>

            {/* Detailed Breakdown */}
            <h3 style={{ margin: "2rem 0 1rem", fontSize: "1.125rem", fontWeight: 600, color: "#0f172a" }}>
              Detailed Breakdown
            </h3>
            <div className="row g-4">
              <div className="col-lg-6">
                {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Top Countries Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <BreakdownList data={byCountry} title={`Top ${TOP_N} Countries`} icon="🌍" color="#6366f1" />
              )}
              </div>
              <div className="col-lg-6">
                              {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Top Device Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <BreakdownList data={byDevice} title={`Top ${TOP_N} Devices`} icon="💻" color="#22c55e" />
              )}
              </div>
                <div className="col-lg-6">
                  {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Top Refferers Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <BreakdownList data={os} title={`Top ${TOP_N} Platform`} icon="🖥️" color="#8b5cf6" />
              )}
              </div>
              <div className="col-lg-6">
                              {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Top Browser Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <BreakdownList data={byBrowser} title={`Top ${TOP_N} Browsers`} icon="🌐" color="#f59e0b" />
              )}
              </div>
              <div className="col-lg-6">
                              {!access?.advancedCharts ? (
                <UpgradeOverlay
                  title="Advanced Analytics Locked"
                  description="Top Refferers Details"
                  onUpgrade={() => navigate("/account/upgrade")}
                />
              ) : (
                <BreakdownList data={referrers} title={`Top ${TOP_N} Referrers`} icon="🔗" color="#0d6efd" />
              )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    <MiniFooter/>
    </>
  )
}